import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HardHat, X } from 'lucide-react';
import VendorWorkTypeSection from './VendorWorkTypeSection';
import OtherQuotesPanel from './OtherQuotesPanel';
import SupplierPublishReviewModal from './SupplierPublishReviewModal';
import TradeBriefingLinksContent from '../pm/TradeBriefingLinksContent';
import {
  CLOSED_PUBLISH_REVIEW,
  useDealSupplierRfqWorkflow,
} from '../../hooks/useDealSupplierRfqWorkflow';
import '../../pages/SupplierRfqAdminPage.css';
import './DealSupplierRfqPanels.css';

const PdfViewerModal = lazy(() => import('../pdf/PdfViewerModal'));

type DealSupplierRfqPanelsProps = {
  dealId: string;
  timberProjectId?: string;
  /** Project Hub timber project PM — also enables quote upload for that PM. */
  hubProjectManagerUid?: string;
  /** Open Files → RFQs request wizard with these staged releases preselected (RFQ requests pilot). */
  onOpenFilesRequest?: (releaseIds: string[]) => void;
};

const DealSupplierRfqPanels: React.FC<DealSupplierRfqPanelsProps> = ({
  dealId,
  timberProjectId,
  hubProjectManagerUid,
  onOpenFilesRequest,
}) => {
  const [searchParams] = useSearchParams();
  const highlightSubmissionId = (searchParams.get('submission') || '').trim();
  const highlightedSubmissionRef = useRef<string | null>(null);
  const openedSubmissionRef = useRef<string | null>(null);
  const workflow = useDealSupplierRfqWorkflow(dealId);
  const {
    supplierOrgs,
    supplierInvites,
    supplierOrgById,
    timberProject,
    cncProject,
    otherProject,
    selectedDeal,
    selectedTimberProject,
    dealVendorSummaries,
    versionsByDeal,
    publishBusyId,
    publishReview,
    setPublishReview,
    packageTitleDraftByProject,
    setPackageTitleDraftByProject,
    versionPickByProject,
    setVersionPickByProject,
    updateRfqProject,
    deleteRfqPackage,
    openSubmissionFile,
    submissionPdfViewer,
    closeSubmissionPdfViewer,
    deleteSubmission,
    loadVersions,
    handleSyncHubLink,
    requestPublish,
    requestTimberPublishReview,
    requestCncPublishReview,
    requestScopeChangeReview,
    dismissScopeChange,
    handlePublishReviewConfirm,
    projectSite,
    projectDisplay,
  } = workflow;

  useEffect(() => {
    if (!highlightSubmissionId) return;
    const sub = workflow.rfqSubmissions.find((s) => s.id === highlightSubmissionId);
    if (!sub) return;
    const embed = searchParams.get('notifEmbed') === '1';
    if (!embed && openedSubmissionRef.current !== highlightSubmissionId) {
      openedSubmissionRef.current = highlightSubmissionId;
      void openSubmissionFile(sub);
    }
    if (highlightedSubmissionRef.current === highlightSubmissionId) return;
    let cancelled = false;
    let timer = 0;
    const started = Date.now();
    const tryScroll = () => {
      if (cancelled || highlightedSubmissionRef.current === highlightSubmissionId) return;
      const el = document.querySelector(
        `[data-rfq-submission-id="${CSS.escape(highlightSubmissionId)}"]`,
      );
      if (el instanceof HTMLElement && el.getClientRects().length > 0) {
        highlightedSubmissionRef.current = highlightSubmissionId;
        el.classList.add('rfq-submission-highlight');
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
        window.setTimeout(() => el.classList.remove('rfq-submission-highlight'), 4000);
        return;
      }
      if (Date.now() - started < 10000) timer = window.setTimeout(tryScroll, 200);
    };
    tryScroll();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [highlightSubmissionId, workflow.rfqSubmissions, openSubmissionFile, searchParams]);

  if (!dealVendorSummaries) return null;

  return (
    <div className="deal-supplier-rfq-panels">
      <div className="srfq-work-type-sections">
        <VendorWorkTypeSection
          workType="timber_rfq"
          dealId={dealId}
          project={timberProject}
          workSummary={dealVendorSummaries.timber}
          supplierOrgs={supplierOrgs}
          supplierOrgById={supplierOrgById}
          rfqPackages={workflow.rfqPackages}
          rfqSubmissions={workflow.rfqSubmissions}
          versionsByDeal={versionsByDeal}
          publishBusyId={publishBusyId}
          packageTitleDraftByProject={packageTitleDraftByProject}
          versionPickByProject={versionPickByProject}
          onUpdateProject={updateRfqProject}
          onSyncHubLink={proj => void handleSyncHubLink(proj)}
          onRequestPublish={requestPublish}
          onRequestTimberPublishReview={(proj, targets, release, preload) =>
            void requestTimberPublishReview(proj, targets, release, preload)
          }
          onResendScopeChange={(proj, orgId, release, wt) =>
            void requestScopeChangeReview(proj, orgId, release, wt)
          }
          onDismissScopeChange={(proj, orgId, release, wt) =>
            dismissScopeChange(proj, orgId, release, wt)
          }
          onDeletePackage={id => void deleteRfqPackage(id)}
          onOpenSubmission={sub => void openSubmissionFile(sub)}
          onDeleteSubmission={deleteSubmission}
          onLoadVersions={loadVersions}
          onPackageTitleChange={(projectId, title) =>
            setPackageTitleDraftByProject(m => ({ ...m, [projectId]: title }))
          }
          onVersionPickChange={(projectId, pick) =>
            setVersionPickByProject(m => ({ ...m, [projectId]: pick }))
          }
          projectSite={projectSite}
          supplierInvites={supplierInvites}
          projectNumber={projectDisplay.numberDisplay || undefined}
          projectName={projectDisplay.descriptor || undefined}
          cncRfqProject={cncProject}
          hubProjectManagerUid={hubProjectManagerUid}
          onOpenFilesRequest={onOpenFilesRequest}
        />
        <VendorWorkTypeSection
          workType="cnc_quote"
          dealId={dealId}
          project={cncProject}
          timberRfqProject={timberProject}
          workSummary={dealVendorSummaries.cnc}
          supplierOrgs={supplierOrgs}
          supplierOrgById={supplierOrgById}
          rfqPackages={workflow.rfqPackages}
          rfqSubmissions={workflow.rfqSubmissions}
          versionsByDeal={versionsByDeal}
          publishBusyId={publishBusyId}
          packageTitleDraftByProject={packageTitleDraftByProject}
          versionPickByProject={versionPickByProject}
          onUpdateProject={updateRfqProject}
          onSyncHubLink={proj => void handleSyncHubLink(proj)}
          onRequestPublish={requestPublish}
          onRequestCncPublishReview={(proj, targets, release) =>
            void requestCncPublishReview(proj, targets, release)
          }
          onResendScopeChange={(proj, orgId, release, wt) =>
            void requestScopeChangeReview(proj, orgId, release, wt)
          }
          onDismissScopeChange={(proj, orgId, release, wt) =>
            dismissScopeChange(proj, orgId, release, wt)
          }
          onDeletePackage={id => void deleteRfqPackage(id)}
          onOpenSubmission={sub => void openSubmissionFile(sub)}
          onDeleteSubmission={deleteSubmission}
          onLoadVersions={loadVersions}
          onPackageTitleChange={(projectId, title) =>
            setPackageTitleDraftByProject(m => ({ ...m, [projectId]: title }))
          }
          onVersionPickChange={(projectId, pick) =>
            setVersionPickByProject(m => ({ ...m, [projectId]: pick }))
          }
          projectSite={projectSite}
          supplierInvites={supplierInvites}
          projectNumber={projectDisplay.numberDisplay || undefined}
          projectName={projectDisplay.descriptor || undefined}
          hubProjectManagerUid={hubProjectManagerUid}
        />
        {selectedDeal && (
          <OtherQuotesPanel
            deal={selectedDeal}
            timberProject={selectedTimberProject}
            project={otherProject}
            packages={workflow.rfqPackages}
            submissions={workflow.rfqSubmissions}
            supplierOrgById={supplierOrgById}
            supplierInvites={supplierInvites}
            hubProjectManagerUid={hubProjectManagerUid}
            projectNumber={projectDisplay.numberDisplay || undefined}
            projectName={projectDisplay.descriptor || undefined}
            onUpdateProject={updateRfqProject}
            onOpenSubmission={sub => void openSubmissionFile(sub)}
            onDeleteSubmission={deleteSubmission}
          />
        )}
        <section className="srfq-work-type-section deal-trades-section" aria-labelledby="deal-trades-heading">
          <header className="srfq-work-type-section-head">
            <h3 id="deal-trades-heading" className="srfq-work-type-section-title">
              <HardHat size={16} aria-hidden />
              Trades
            </h3>
          </header>
          <div className="srfq-work-type-body deal-trades-body">
            {timberProjectId ? (
              <TradeBriefingLinksContent dealId={dealId} timberProjectId={timberProjectId} />
            ) : (
              <div className="srfq-work-type-empty">
                <p>No Project Hub project is linked to this deal.</p>
                <p className="srfq-work-type-empty-hint">
                  Trade briefings need a hub project so files and structure scope can be shared.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      {(publishReview.loading || (publishReview.error && !publishReview.data)) && (
        <div
          className="srfq-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!publishReview.loading) setPublishReview(CLOSED_PUBLISH_REVIEW);
          }}
        >
          <div
            className="srfq-modal srfq-modal--csv-preview"
            role="dialog"
            aria-labelledby="srfq-publish-review-status"
            onClick={e => e.stopPropagation()}
          >
            <div className="srfq-modal-head">
              <h3 id="srfq-publish-review-status">Review &amp; publish</h3>
              {!publishReview.loading && (
                <button
                  type="button"
                  className="srfq-icon-btn"
                  aria-label="Close"
                  onClick={() => setPublishReview(CLOSED_PUBLISH_REVIEW)}
                >
                  <X size={18} />
                </button>
              )}
            </div>
            {publishReview.loading && (
              <p className="srfq-muted srfq-help">Loading publish preview…</p>
            )}
            {publishReview.error && (
              <>
                <p className="srfq-banner srfq-banner--error">{publishReview.error}</p>
                <div className="srfq-modal-actions">
                  <button
                    type="button"
                    className="srfq-btn primary"
                    onClick={() => setPublishReview(CLOSED_PUBLISH_REVIEW)}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <SupplierPublishReviewModal
        open={!!publishReview.data}
        data={publishReview.data}
        supplierOrgById={supplierOrgById}
        supplierInvites={supplierInvites}
        projectSite={projectSite}
        timberRfqProject={timberProject}
        cncRfqProject={cncProject}
        publishing={!!publishReview.pending && publishBusyId === publishReview.pending.proj.id}
        onClose={() => setPublishReview(CLOSED_PUBLISH_REVIEW)}
        onConfirm={handlePublishReviewConfirm}
      />

      {submissionPdfViewer && (
        <Suspense fallback={null}>
          <PdfViewerModal
            key={submissionPdfViewer.documentId}
            url={submissionPdfViewer.url}
            title={submissionPdfViewer.title}
            documentId={submissionPdfViewer.documentId}
            leadId={submissionPdfViewer.leadId}
            leadName={projectDisplay.clientLabel || projectDisplay.descriptor || 'Supplier quote'}
            dealId={submissionPdfViewer.dealId}
            projectId={submissionPdfViewer.projectId || timberProjectId}
            disableTakeoff
            onClose={closeSubmissionPdfViewer}
          />
        </Suspense>
      )}
    </div>
  );
};

export default DealSupplierRfqPanels;
